import { ChangeDetectionStrategy, Component, ElementRef, ViewEncapsulation, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { MarkdownComponent } from 'ngx-markdown';
import { ActivityEntityName } from '@jms/model';
import { EntityRoute, buildEntityManageRoute } from '@jms/utils/util.pipes.standalone';

import type { UIMessage } from 'ai';

// Custom URL scheme Workbuddy Agent emits for inline record links: entity://<Type>/<id>.
// Angular's DomSanitizer rewrites unknown schemes to "unsafe:entity://..." in the
// rendered anchor href, so parsing must tolerate that prefix.
const ENTITY_LINK_SCHEME = 'entity://';
const SANITIZER_UNSAFE_PREFIX = 'unsafe:';

// Most ActivityEntityName values are themselves space-free and can be used directly as
// link tokens (e.g. "Job", "Invoice", "PO"), so the decoded token IS the entity value.
// The only exceptions are entities whose enum value contains a space, which cannot live
// in a URL path segment without breaking marked's link parsing; those use a space-free
// token here that maps to the real enum value.
const ENTITY_LINK_TOKEN_OVERRIDES: Record<string, ActivityEntityName> = {
  QuotePro: ActivityEntityName.QuotePro,
  SitePro: ActivityEntityName.SitePro,
};

// Class added to recognized (routable) entity anchors so the .less can style every
// entity chip off this single signal, instead of maintaining a per-token selector list.
const ENTITY_CHIP_CLASS = 'entity-chip';

// Data attribute that preserves the original entity:// href after we rewrite the visible
// href to a canonical in-app URL (for correct new-tab/middle-click behaviour). The
// left-click handler resolves the route from this attribute so SPA navigation keeps working.
const ENTITY_HREF_DATA_ATTR = 'data-entity-href';

// The set of valid ActivityEntityName values, used to validate a decoded token before
// treating it as an entity.
const ACTIVITY_ENTITY_NAMES = new Set<string>(Object.values(ActivityEntityName));

@Component({
  selector: 'agent-agent-message',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [MarkdownComponent],
  templateUrl: './agent.message.component.html',
  styleUrls: ['./agent.message.component.less'],
  host: { '(click)': 'onContainerClick($event)' },
})
export class AgentAgentMessageComponent {

  private readonly router = inject(Router);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  public $message = input.required<UIMessage>({ alias: 'message' });
  public $revealedText = input<string>('', { alias: 'revealedText' });

  // ngx-markdown renders asynchronously: MarkdownComponent.render() awaits MarkdownService.parse(),
  // whose sanitize step is async, so the parsed HTML is written to innerHTML in a microtask AFTER
  // the synchronous change-detection/afterRender phase. An afterRenderEffect therefore always runs
  // BEFORE the new markdown DOM exists, and the async innerHTML write then clobbers any href rewrite
  // we make. ngx-markdown emits (ready) synchronously right after it assigns innerHTML (on the first
  // render and on every re-render/stream tick), so that is the correct moment to (re)tag chips and
  // rewrite hrefs. This guarantees the visible href is the canonical in-app URL after every render.
  protected onMarkdownReady(): void {

    this.tagEntityChips();
    this.wrapOverflowingTables();
  }

  // Markdown tables wider than the chat column must scroll horizontally inside their
  // own container — the message body itself must never scroll sideways. Each (ready)
  // re-render rebuilds the markdown DOM, so wrapping re-applies idempotently.
  private wrapOverflowingTables(): void {

    const tables = this.host.nativeElement.querySelectorAll('table');
    tables.forEach((table) => {
      if (table.parentElement?.classList.contains('table-scroll')) return;
      const wrapper = document.createElement('div');
      wrapper.className = 'table-scroll';
      table.parentNode?.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    });
  }

  // Marks anchors with a resolvable entity:// destination so they render as chips, and
  // un-marks any that no longer resolve (e.g. content changed mid-stream). For resolvable
  // anchors we also rewrite the raw entity:// href to the canonical in-app URL so middle-,
  // cmd-/ctrl-click and "open in new tab" (which bypass the left-click handler) open the
  // correct record instead of the unsafe entity:// scheme. Left-click still goes through
  // onContainerClick for SPA navigation. Entity anchors that cannot resolve (blank or
  // malformed id, unknown type) are unwrapped to plain text so the user never sees a dead
  // link; non-entity anchors are left untouched.
  private tagEntityChips(): void {

    const anchors = this.host.nativeElement.querySelectorAll('a');
    anchors.forEach((anchor) => {
      // Resolve from the preserved original entity:// href if we already rewrote this anchor,
      // otherwise from the current href (a freshly rendered, not-yet-rewritten anchor).
      const sourceHref = anchor.getAttribute(ENTITY_HREF_DATA_ATTR) ?? anchor.getAttribute('href');
      const route = this.resolveEntityRoute(sourceHref);
      anchor.classList.toggle(ENTITY_CHIP_CLASS, route !== null);

      // A broken entity ref has no valid destination anywhere — strip the anchor and keep
      // its readable label. Each markdown (ready) re-render rebuilds the DOM, so a link that
      // only looks broken mid-stream comes back once its href finishes streaming.
      if (!route && this.isEntityHref(sourceHref)) {
        anchor.replaceWith(...Array.from(anchor.childNodes));
        return;
      }

      if (route?.routerLink) {
        // Preserve the original entity:// href for the left-click handler, then rewrite the
        // visible href to the canonical in-app URL so new-tab/middle-click opens the record.
        if (!anchor.hasAttribute(ENTITY_HREF_DATA_ATTR) && sourceHref) {
          anchor.setAttribute(ENTITY_HREF_DATA_ATTR, sourceHref);
        }
        const urlTree = this.router.createUrlTree(route.routerLink, route.queryParams ? { queryParams: route.queryParams } : {});
        anchor.setAttribute('href', this.router.serializeUrl(urlTree));
      }
    });
  }

  // Single delegated click handler for the whole rendered message (one listener,
  // not one per link). Intercepts only entity:// anchors, resolves them through
  // the canonical route map, and navigates via the Angular Router. Normal links
  // and ordinary text are left untouched.
  protected onContainerClick(event: MouseEvent): void {

    const target = event.target;
    if (!(target instanceof Element)) return;

    const anchor = target.closest('a');
    if (!anchor) return;

    // Prefer the preserved entity:// href: tagEntityChips rewrites the visible href to a
    // canonical in-app URL, so the original entity link only survives in the data attribute.
    const sourceHref = anchor.getAttribute(ENTITY_HREF_DATA_ATTR) ?? anchor.getAttribute('href');
    const route = this.resolveEntityRoute(sourceHref);
    if (!route?.routerLink) return;

    // It is an entity link with a real destination: take over navigation entirely
    // so the browser never follows the raw entity:// href.
    event.preventDefault();
    this.router.navigate(route.routerLink, route.queryParams ? { queryParams: route.queryParams } : undefined);
  }

  // True when the href uses the entity:// scheme at all, resolvable or not — used to
  // tell a broken entity ref (strip to text) apart from an ordinary external link.
  private isEntityHref(rawHref: string | null): boolean {

    if (!rawHref) return false;

    const href = rawHref.startsWith(SANITIZER_UNSAFE_PREFIX)
      ? rawHref.slice(SANITIZER_UNSAFE_PREFIX.length)
      : rawHref;

    return href.startsWith(ENTITY_LINK_SCHEME);
  }

  // Parses an entity:// href into a real router destination, or null when the href
  // is not an entity link, is malformed, or maps to a type that has no detail page.
  // The sanitizer's "unsafe:" prefix is stripped before parsing.
  private resolveEntityRoute(rawHref: string | null): EntityRoute | null {

    if (!rawHref) return null;

    const href = rawHref.startsWith(SANITIZER_UNSAFE_PREFIX)
      ? rawHref.slice(SANITIZER_UNSAFE_PREFIX.length)
      : rawHref;

    if (!href.startsWith(ENTITY_LINK_SCHEME)) return null;

    const path = href.slice(ENTITY_LINK_SCHEME.length);
    const separatorIndex = path.indexOf('/');
    if (separatorIndex <= 0) return null;

    const typeToken = decodeURIComponent(path.slice(0, separatorIndex));
    const id = decodeURIComponent(path.slice(separatorIndex + 1));
    if (!id) return null;

    // Space-free override tokens map to enum values that contain spaces; everything else
    // is used directly as the entity value when it is a recognized ActivityEntityName.
    const entity = ENTITY_LINK_TOKEN_OVERRIDES[typeToken]
      ?? (ACTIVITY_ENTITY_NAMES.has(typeToken) ? (typeToken as ActivityEntityName) : undefined);
    if (!entity) return null;

    const route = buildEntityManageRoute(entity, id);
    return route.routerLink ? route : null;
  }
}
